import React, { useEffect, useState } from "react";
import { 
  GraduationCap, Briefcase, Code, Monitor, Factory, Palette, 
  Lightbulb, Train, FileText, Users, Languages, Rocket, ArrowRight,
  X, CheckCircle, Loader2, MapPin, School
} from "lucide-react";
import { useTheme } from "../ThemeContext";
// Importing the API service
import { submitContactForm } from "../../../services/contactApi";

const Training = () => {
  const { isDarkMode, theme } = useTheme();
  const activeMode = isDarkMode || theme === 'dark';
  
  // --- Animation State ---
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // --- Lead Gen / Modal State ---
  const [showModal, setShowModal] = useState(false);
  const [pendingLink, setPendingLink] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Form State matching your Go Backend Struct
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    class: "", // Education/Class level
    subject: "", // Will be auto-filled with Course Name
    message: "Interested in accessing course details.",
    country: "India" // Default
  });

  // Check if user has already submitted a lead in this session/browser
  const isLeadUnlocked = () => {
    return localStorage.getItem("aespl_lead_unlocked") === "true";
  };

  const handleCardClick = (link, courseName) => {
    if (isLeadUnlocked()) {
      // Already submitted before? Just open the link.
      window.open(`https://aespleduzone.com${link}`, '_blank');
    } else {
      // Not submitted? Trigger the mandatory form.
      setPendingLink(link);
      setFormData(prev => ({ ...prev, subject: `Inquiry: ${courseName}` }));
      setShowModal(true);
      setErrorMsg("");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg("");

    try {
      // 1. Send data to backend
      await submitContactForm(formData);

      // 2. On success
      setSubmitSuccess(true);
      localStorage.setItem("aespl_lead_unlocked", "true");

      // 3. Wait a moment for visual success, then redirect
      setTimeout(() => {
        window.open(`https://aespleduzone.com${pendingLink}`, '_blank');
        setShowModal(false);
        setSubmitSuccess(false);
        setIsSubmitting(false);
      }, 1500);

    } catch (error) {
      console.error(error);
      setErrorMsg(error.message || "Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  const sections = [
    {
      id: "college",
      title: "Training & Skills",
      description: "Industry-standard upskilling for college youths and professionals.",
      gradient: "from-purple-500 via-fuchsia-500 to-pink-500",
      icon: <GraduationCap />,
      items: [
        { name: "Professional Courses", icon: <Briefcase />, link: "/career-courses/professional-courses" },
        { name: "IT Courses", icon: <Code />, link: "/career-courses/it-courses" },
        { name: "Technical Courses", icon: <Monitor />, link: "/career-courses/technical-courses" },
        { name: "Industrial Courses", icon: <Factory />, link: "/industrial-courses" },
        { name: "Hobby Courses", icon: <Palette />, link: "/career-courses/hobby-courses" },
        { name: "EDP Courses", icon: <Lightbulb />, link: "/course/entrepreneurship-development-program" },
        { name: "Industrial Tour", icon: <Train />, link: "/industrial-tour" },
        { name: "Project Report", icon: <FileText />, link: "/project-reports" },
        { name: "Career Counselling", icon: <Users />, link: "/school-courses/career-counseling-school" },
        { name: "Language Courses", icon: <Languages />, link: "/school-courses/orientation-courses" },
      ],
    },
    {
      id: "entrepreneurship",
      title: "Entrepreneurship Hub",
      description: "Turn your vision into reality with our business incubator resources.",
      gradient: "from-orange-500 via-amber-500 to-red-500",
      icon: <Rocket />,
      items: [
        { name: "Startup Courses", icon: <Rocket />, link: "/course/entrepreneurship-development-program" },
        { name: "Industrial Docs", icon: <Factory />, link: "/industrial-documentry" },
        { name: "Project Reports", icon: <FileText />, link: "/project-reports" },
        { name: "Business Idea", icon: <Lightbulb />, link: "/business-idea" },
        { name: "Industrial Episodes", icon: <Factory />, link: "/industrial-courses" },
        { name: "Prof. Episodes", icon: <Briefcase />, link: "/career-courses/professional-courses" },
        { name: "Creative Skills", icon: <Palette />, link: "/career-courses/hobby-courses" },
        { name: "Tech Stack", icon: <Code />, link: "/career-courses/it-courses" },
      ],
    },
  ];

  return (
    <div 
      className={`min-h-screen relative overflow-hidden transition-colors duration-700 ease-in-out font-sans selection:bg-pink-500 selection:text-white
      ${activeMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}
    >
      {/* --- CSS for Custom Animations --- */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
          100% { transform: translateY(0px) scale(1); }
        }
        @keyframes float-delayed {
          0% { transform: translateY(0px) scale(1.05); }
          50% { transform: translateY(20px) scale(1); }
          100% { transform: translateY(0px) scale(1.05); }
        }
        .animate-float { animation: float 10s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 12s ease-in-out infinite; }
        
        .fade-in-up {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .fade-in-up.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* 3D Tilt Effect on Cards */
        .card-perspective {
          perspective: 1000px;
        }
        .card-inner {
          transition: transform 0.4s ease, box-shadow 0.4s ease;
        }
        .card-perspective:hover .card-inner {
          transform: rotateX(2deg) rotateY(-2deg) translateY(-5px);
          box-shadow: 0 20px 40px -10px rgba(236, 72, 153, 0.2);
        }
      `}</style>

      {/* --- Dynamic Background --- */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-20 -right-20 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 animate-float
          ${activeMode ? 'bg-gradient-to-br from-pink-800 to-purple-900' : 'bg-gradient-to-br from-pink-300 to-purple-300'}`} />
        <div className={`absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 animate-float-delayed
          ${activeMode ? 'bg-gradient-to-tr from-orange-800 to-red-900' : 'bg-gradient-to-tr from-orange-300 to-red-300'}`} />
        <div className={`absolute inset-0 opacity-[0.03] ${activeMode ? 'bg-[url("https://www.transparenttextures.com/patterns/cubes.png")] invert' : 'bg-[url("https://www.transparenttextures.com/patterns/cubes.png")]'}`}></div>
      </div>

      {/* --- Main Content --- */}
      <div className="relative z-10 pt-28 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-20">
        
        {/* Header Section */}
        <div className={`text-center max-w-3xl mx-auto fade-in-up ${mounted ? 'visible' : ''}`} style={{ transitionDelay: '0ms' }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6 shadow-sm hover:border-pink-500/30 transition-colors">
            <span className={`text-xs font-bold tracking-wider uppercase bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-orange-500`}>
              Unlock Your Potential
            </span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
            <span className="block">Professional</span>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 animate-pulse">
              Training Hub
            </span>
          </h1>
          
          <p className={`text-lg sm:text-xl md:text-2xl leading-relaxed ${activeMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Bridge the gap between academic theory and industry reality with our expert-led skill development programs.
          </p>
        </div>

        {/* Sections Loop */}
        {sections.map((section, sectionIdx) => (
          <div 
            key={section.id} 
            className={`fade-in-up ${mounted ? 'visible' : ''}`} 
            style={{ transitionDelay: `${(sectionIdx + 1) * 150}ms` }}
          >
            {/* Section Title */}
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-10 border-l-4 border-transparent pl-4 md:pl-0 md:border-l-0">
              <div className={`hidden md:flex w-14 h-14 items-center justify-center rounded-2xl bg-gradient-to-br ${section.gradient} text-white shadow-lg shadow-pink-500/20 transform rotate-3 ring-4 ring-white/5`}>
                {React.cloneElement(section.icon, { className: "w-7 h-7" })}
              </div>
              <div>
                <h2 className={`text-3xl sm:text-4xl font-bold flex items-center gap-3 ${activeMode ? 'text-white' : 'text-gray-900'}`}>
                  <span className="md:hidden text-pink-500">{section.icon}</span>
                  {section.title}
                </h2>
                <p className={`mt-2 text-base sm:text-lg ${activeMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {section.description}
                </p>
              </div>
              <div className={`hidden md:block h-[1px] flex-grow ml-6 bg-gradient-to-r ${activeMode ? 'from-gray-800 to-transparent' : 'from-gray-200 to-transparent'}`} />
            </div>

            {/* Grid of Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {section.items.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => handleCardClick(item.link, item.name)}
                  className="group relative cursor-pointer card-perspective h-full"
                >
                  {/* Card Content */}
                  <div className={`card-inner relative h-full flex flex-col p-6 rounded-2xl border backdrop-blur-md transition-all duration-300
                    ${activeMode 
                      ? 'bg-gray-900/60 border-gray-800 hover:border-gray-700 hover:bg-gray-800/80' 
                      : 'bg-white/60 border-gray-200 hover:border-pink-200 hover:bg-white'}
                  `}>
                    
                    {/* Gradient Overlay on Hover */}
                    <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-300 bg-gradient-to-br ${section.gradient}`}></div>

                    {/* Icon Box */}
                    <div className="flex items-start justify-between mb-4 relative z-10">
                      <div className={`p-3 rounded-xl transition-colors duration-300 
                        ${activeMode 
                          ? 'bg-gray-800 text-gray-300 group-hover:bg-black group-hover:text-white group-hover:shadow-[0_0_15px_rgba(236,72,153,0.5)]' 
                          : 'bg-gray-100 text-gray-600 group-hover:bg-pink-50 group-hover:text-pink-600'}
                      `}>
                        {React.cloneElement(item.icon, { className: "w-6 h-6" })}
                      </div>
                      
                      <ArrowRight className={`w-5 h-5 transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 
                        ${activeMode ? 'text-gray-400' : 'text-gray-400'}`} 
                      />
                    </div>

                    {/* Text */}
                    <div className="relative z-10">
                      <h3 className={`text-lg font-bold mb-1 transition-colors group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r ${section.gradient}
                        ${activeMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {item.name}
                      </h3>
                      
                      <p className={`text-xs font-medium uppercase tracking-wider transition-colors
                        ${activeMode ? 'text-gray-500 group-hover:text-gray-400' : 'text-gray-400 group-hover:text-gray-600'}`}>
                        View Details
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* --- LEAD GENERATION MODAL --- */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowModal(false)} // Optional: Allow close on backdrop click? Prompt said Mandatory, usually means forcing interaction. But good UX allows cancel.
          ></div>

          {/* Modal Content */}
          <div className={`relative w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden border transform transition-all animate-in fade-in zoom-in duration-300
            ${activeMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}
          `}>
            
            {/* Modal Header */}
            <div className="relative px-8 pt-8 pb-4">
               <button 
                onClick={() => setShowModal(false)}
                className={`absolute top-4 right-4 p-2 rounded-full transition-colors 
                ${activeMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X size={20} />
              </button>
              
              <h2 className={`text-2xl font-bold text-center ${activeMode ? 'text-white' : 'text-gray-900'}`}>
                {submitSuccess ? 'Access Granted!' : 'One Step Away'}
              </h2>
              <p className={`text-center mt-2 text-sm ${activeMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {submitSuccess 
                  ? 'Redirecting you to the course...' 
                  : 'Please enter your details to unlock full access to this course material.'}
              </p>
            </div>

            {/* Modal Body */}
            <div className="px-8 pb-8">
              {submitSuccess ? (
                 <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-green-500" />
                    </div>
                    <p className="text-green-500 font-medium animate-pulse">Redirecting...</p>
                 </div>
              ) : (
                <form onSubmit={onFormSubmit} className="space-y-4">
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className={`text-xs font-semibold uppercase tracking-wide ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>First Name</label>
                      <input 
                        required
                        type="text" 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-pink-500/50 transition-all
                          ${activeMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className={`text-xs font-semibold uppercase tracking-wide ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>Last Name</label>
                      <input 
                        type="text" 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-pink-500/50 transition-all
                          ${activeMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-1">
                    <label className={`text-xs font-semibold uppercase tracking-wide ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>Email Address</label>
                    <input 
                      required
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-pink-500/50 transition-all
                        ${activeMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className={`text-xs font-semibold uppercase tracking-wide ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>Phone Number</label>
                    <input 
                      required
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-pink-500/50 transition-all
                        ${activeMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  {/* Extra Lead Gen Fields (Class/City) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <School size={12}/> Qualification/Class
                      </label>
                      <input 
                        type="text" 
                        name="class"
                        value={formData.class}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-pink-500/50 transition-all
                          ${activeMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                        placeholder="e.g. B.Tech / 12th"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <MapPin size={12} /> City
                      </label>
                      <input 
                        type="text" 
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-pink-500/50 transition-all
                          ${activeMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                        placeholder="e.g. Noida"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {errorMsg && (
                    <p className="text-red-500 text-sm bg-red-500/10 p-2 rounded-lg text-center">{errorMsg}</p>
                  )}

                  {/* Submit Button */}
                  <button
                    disabled={isSubmitting}
                    className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-pink-600 to-orange-600 text-white font-bold tracking-wide shadow-lg shadow-pink-500/25 hover:shadow-pink-500/40 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin w-5 h-5" /> Processing...
                      </>
                    ) : (
                      "Unlock Access"
                    )}
                  </button>
                  
                  <p className={`text-[10px] text-center ${activeMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    Your information is secure. By continuing, you agree to receive course details.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={`mt-20 pt-10 border-t text-center fade-in-up ${mounted ? 'visible' : ''} style={{ transitionDelay: '500ms' }}
        ${activeMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <p className={`text-sm ${activeMode ? 'text-gray-500' : 'text-gray-400'}`}>
          © {new Date().getFullYear()} AESPL Education Zone. Empowering Future Leaders.
        </p>
      </div>
    </div>
  );
};

export default Training;